import React, { useMemo, useState } from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { Textarea } from '../ui/textarea';
import { FieldHeader, FieldErrors, FieldHelpText } from './FieldParts';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

/** Coerce a configForm-stored numeric (always a string) to a positive int or undefined. */
const parsePositiveInt = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.floor(n);
};

type AllowedInput = 'all' | 'numbers' | 'letters';

/** Strip characters not permitted by the allowedInput mode. */
const filterAllowedInput = (raw: string, mode: AllowedInput): string => {
  if (mode === 'numbers') return raw.replace(/[^0-9]/g, '');
  if (mode === 'letters') return raw.replace(/[^A-Za-z\s]/g, '');
  return raw;
};

const LongTextInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [touched, setTouched] = useState(false);
  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    rows?: number;
    minLength?: number | string;
    maxLength?: number | string;
    allowedInput?: AllowedInput;
    pattern?: string;
    patternErrorMessage?: string;
    showCharacterCounter?: boolean;
    defaultValue?: string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'Long Text';
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || 'Enter your text...';
  const required = data?.required || false;
  const helpText = data?.helpText;
  const rows = data?.rows || 4;
  const minLength = parsePositiveInt(data?.minLength);
  const maxLength = parsePositiveInt(data?.maxLength);
  const allowedInput: AllowedInput =
    data?.allowedInput === 'numbers' || data?.allowedInput === 'letters'
      ? data.allowedInput
      : 'all';
  const pattern = data?.pattern?.trim() || undefined;
  const patternErrorMessage = data?.patternErrorMessage?.trim() || undefined;
  const showCounter = !!data?.showCharacterCounter;
  const currentLength = (displayValue || '').length;

  const errors = useMemo<string[]>(() => {
    if (isEditor || !touched) return [];
    const out: string[] = [];
    const v = value || '';
    const len = v.length;
    if (required && len === 0) {
      out.push('This field is required');
    }
    if (minLength !== undefined && len > 0 && len < minLength) {
      out.push(
        `Must be at least ${minLength} character${minLength === 1 ? '' : 's'}`,
      );
    }
    if (maxLength !== undefined && len > maxLength) {
      out.push(
        `Must be at most ${maxLength} character${maxLength === 1 ? '' : 's'}`,
      );
    }
    if (pattern && len > 0) {
      try {
        if (!new RegExp(pattern).test(v)) {
          out.push(patternErrorMessage || 'Invalid format');
        }
      } catch {
        // Invalid regex configured by the form author — silently skip so the
        // filler isn't blocked by a broken validation rule.
      }
    }
    return Array.from(new Set(out));
  }, [
    isEditor,
    touched,
    required,
    value,
    minLength,
    maxLength,
    pattern,
    patternErrorMessage,
  ]);

  // Read-only (e.g. a completed result): render the entered value as
  // static text, preserving line breaks — no textarea, no counter.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="whitespace-pre-wrap text-sm">
          {displayValue ? (
            displayValue
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <Textarea
        id={element.id}
        placeholder={placeholder}
        value={displayValue || ''}
        onChange={(e) => {
          setTouched(true);
          setValue(filterAllowedInput(e.target.value, allowedInput));
        }}
        onBlur={() => setTouched(true)}
        rows={rows}
        minLength={minLength}
        maxLength={maxLength}
        className="w-full resize-y"
      />
      {showCounter && (
        <p className="text-right text-xs text-muted-foreground">
          {maxLength !== undefined
            ? `${currentLength} / ${maxLength}`
            : `${currentLength} character${currentLength === 1 ? '' : 's'}`}
        </p>
      )}
      <FieldErrors errors={errors} />
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default LongTextInput;
